import React, { useEffect, useMemo, useState } from "react";

const BASE = `${process.env.PUBLIC_URL}/images`;

const IMAGES = {
  datanex: `${BASE}/datanex.png`,
  eduboxx: `${BASE}/eduboxx.png`,
  eduinsights: `${BASE}/eduinsights.png`,
  eduflux: `${BASE}/eduflux.png`,
  eduaikit: `${BASE}/eduaikit.png`,
  kroton: `${BASE}/kroton.jpg`,
  anhanguera: `${BASE}/anhanguera.jpg`,
  lfg: `${BASE}/lfg.jpg`,
  damasio: `${BASE}/damasio.jpg`,
};

const CONTENT = {
  en: {
    nav: {
      speed: "Full Speed",
      proof: "Proof",
      problem: "Problem",
      solution: "Solution",
      products: "Platform",
      flow: "Flow",
      impact: "Impact",
      cases: "Case Studies",
      licensing: "Brazil Licensing",
      contact: "Contact",
    },
    tagline: "Learning Intelligence. Built on pedagogy. Powered by AI.",
    hero: {
      eyebrow: "DatanexAI • Brazil Master Licensing Agent",
      title:
        "Eduboxx Turns Educational Technology Into Something Governments Can Actually See, Trust, and Deploy",
      subtitle:
        "Eduboxx is the physical deployment of a complete education technology system for Brazil — combining visible infrastructure with personalized learning generated from your teachers' lesson plans, admissions evaluation and placement, and simulation-based challenges, all aligned to the BNCC and built on real pedagogy.",
      primaryCta: "Request a Pilot",
      secondaryCta: "Explore Brazil Rollout",
      stats: [
        "Proven in Brazil",
        "Millions of streams delivered",
        "Thousands of students served",
        "Built for public-sector scale",
      ],
      sideTitle: "Why this message wins",
      sideBullets: [
        "It leads with something tangible and physically understandable.",
        "It transforms a complex platform into one clear deployment story.",
        "It proves the system has already worked at scale in Brazil.",
        "It creates urgency without sounding abstract or experimental.",
      ],
    },
    speed: {
      title: "Every student, full speed — without overloading your internet.",
      subtitle:
        "Eduboxx delivers EduFlux's rich, personalized learning to an entire classroom at once — from a local node inside the school, so 30 students streaming lessons and simulations don't bring the connection to its knees.",
      problem:
        "A school can have internet and still stall. One shared connection wasn't built for a whole classroom pulling video, simulations, and personalized content at the same moment. The more engaging the content, the worse the bottleneck. 'Connected' doesn't mean 'ready for 30 students at once.'",
      features: [
        "Concurrent delivery at classroom scale: Eduboxx serves content from a local node over the school's own network, so every student gets full-speed, full-quality learning simultaneously — no buffering, no queue.",
        "Your connection stays free: heavy content is delivered locally; the internet link only carries lightweight sync. Other classes, admin systems, and the school's bandwidth aren't starved.",
        "Built for rich content: video lessons, EduAIKit simulations, Teli's voice — the heavy, engaging media that cloud-only delivery struggles to push to many students at once.",
        "Works when connectivity is limited too: in schools with slow, shared, or unreliable internet, Eduboxx keeps learning running at full quality regardless.",
        "Syncs when it can: progress and signals flow back to the learner record when the connection allows, keeping the record complete.",
      ],
      differentiation:
        "Cloud-only platforms slow down exactly when a classroom needs them most — when everyone's learning at once. Eduboxx delivers to every student at full speed, and leaves your internet connection free to do everything else.",
      cta: "Bring full-speed learning to every classroom →",
    },
    proof: {
      title: "Proven at Scale in Brazil",
      intro:
        "Eduboxx is not a concept. It has already been successfully implemented in major Brazilian education environments, delivering large-scale educational streaming and classroom access in real operational settings.",
      stats: [
        "Millions of streams delivered",
        "Thousands of students reached",
        "Multi-site deployment experience",
        "Validated in real education environments",
      ],
      institutionsTitle: "Implemented in",
      description:
        "These deployments proved that Eduboxx could deliver reliable educational content at scale across complex learning environments — creating the foundation for the more powerful integrated DatanexAI model now being positioned for Brazil.",
    },
    problem: {
      title: "The Problem",
      intro:
        "Education systems want modernization, visibility, and better learning outcomes — but most initiatives fail long before they create real transformation.",
      cards: [
        {
          title: "Infrastructure Reality",
          text: "Many schools and networks still operate in environments where bandwidth, reliability, and simultaneous access remain major constraints.",
        },
        {
          title: "Fragmented Systems",
          text: "Content delivery, analytics, operations, and learning support are often spread across disconnected tools that create friction and confusion.",
        },
        {
          title: "Weak Procurement Narratives",
          text: "Public-sector buyers often struggle to trust or evaluate solutions that are presented only as software, dashboards, or cloud abstractions.",
        },
        {
          title: "Pilot-to-Scale Failure",
          text: "Even promising pilots often collapse because the model was never designed to become a scalable operating system.",
        },
      ],
    },
    solution: {
      title: "Why Eduboxx Wins",
      intro:
        "Eduboxx wins because it starts where trust starts: with something visible, physical, and deployable — then delivers the full platform inside that deployment.",
      cards: [
        {
          title: "Visible Infrastructure",
          text: "Eduboxx gives governments and institutions something tangible to evaluate, deploy, and expand.",
        },
        {
          title: "Integrated by Design",
          text: "EduInsights for admissions evaluation and placement, EduFlux for personalized learning, and EduAIKit for simulation-based challenges — integrated capabilities inside the Eduboxx system, sharing one learner record.",
        },
        {
          title: "Operationally Clear",
          text: "One platform. One deployment path. One story from pilot to scale.",
        },
        {
          title: "Built for Expansion",
          text: "The model is designed to move from real implementations into regional, state, and national rollout opportunities.",
        },
      ],
    },
    products: {
      title: "Inside the Eduboxx Platform",
      intro:
        "Eduboxx is the visible deployment layer. Inside it sits a complete education technology ecosystem — built on pedagogy, powered by AI, and aligned to the BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Physical Infrastructure",
          logoKey: "eduboxx",
          features: [
            "On-site deployment layer for schools and networks",
            "Visible, tangible infrastructure for public-sector confidence",
            "Supports large-scale content delivery in constrained environments",
            "Creates the physical gateway for full platform adoption",
          ],
        },
        {
          name: "EduFlux",
          tag: "Personalized Learning Intelligence",
          logoKey: "eduflux",
          features: [
            "Generative personalization: reads each teacher's lesson plan and creates the lesson, homework, and practice for every student — not pulled from a fixed bank",
            "Aligned to the BNCC: every generated lesson, task, and assessment maps to BNCC habilidades — with per-habilidade reporting so teachers and coordinators see coverage and mastery, not just activity",
            "The Teli tutor inside the work: an AI tutor that guides students through tasks in real time, with voice and text",
            "Develops Power Skills and Core Attributes — the transferable competencies behind frameworks like the IB Approaches to Learning",
          ],
        },
        {
          name: "EduInsights",
          tag: "Admissions Evaluation & Placement",
          logoKey: "eduinsights",
          features: [
            "Signal detection beyond a single test score — surfaces how an applicant thinks, learns, and grows",
            "Fairer, fuller evaluation that compares applicants on more than memorized content",
            "Informs placement and day-one support, not just admit or deny",
            "Feeds the same learner record that follows the student into the classroom",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Simulation-Based Learning · Part of EduFlux",
          logoKey: "eduaikit",
          features: [
            "Simulation-based challenges: students reason through scenarios and make decisions, not just recall facts",
            "Strong in non-STEM too — history, social studies, ethics, the arts — where fixed question banks fall short",
            "Generated from the lesson itself, personalized to each student",
            "Develops reasoning, perspective-taking, evidence use, and communication",
          ],
        },
      ],
      closing:
        "Together, these components make Eduboxx far more than a delivery box. They make it the physical expression of a complete education operating system — generative, pedagogy-led, BNCC-aligned, and built on one learner record that follows each student across every surface.",
    },
    flow: {
      title: "How the System Works",
      intro:
        "The Eduboxx model is designed to move from visible deployment to measurable educational and operational impact.",
      steps: [
        {
          title: "1. Deploy",
          text: "Install Eduboxx in pilot schools, campuses, classrooms, or target regions.",
        },
        {
          title: "2. Deliver",
          text: "Provide reliable access to educational content and services in real school environments.",
        },
        {
          title: "3. Evaluate & Place",
          text: "Use EduInsights to evaluate applicants beyond a single test score and inform placement and day-one support.",
        },
        {
          title: "4. Personalize Learning",
          text: "Use EduFlux to generate personalized lessons from each teacher's plan — BNCC-aligned, with the Teli tutor guiding students through the work.",
        },
        {
          title: "5. Build Reasoning",
          text: "Use EduAIKit to turn lessons into simulation-based challenges across every subject, including non-STEM.",
        },
        {
          title: "6. Scale",
          text: "Expand from pilot implementation into network-wide, municipal, state, or national rollouts.",
        },
      ],
    },
    impact: {
      title: "Results and Impact",
      intro:
        "Eduboxx creates value at multiple levels: operationally, administratively, strategically, and educationally.",
      cards: [
        {
          title: "More Reliable Delivery",
          text: "Stabilize access to educational content across real-world school environments.",
        },
        {
          title: "Personalized Learning at Scale",
          text: "Each student receives instruction generated from your teachers' lesson plans, aligned to the BNCC, with the Teli tutor guiding them through the work.",
        },
        {
          title: "Richer Admissions Signal",
          text: "Admissions teams get fuller signal than a single test score, and what's learned about each applicant informs placement and day-one support.",
        },
        {
          title: "Deeper Reasoning, Every Subject",
          text: "Simulation-based challenges develop transferable competencies — including in non-STEM subjects where fixed question banks fall short.",
        },
      ],
    },
    cases: {
      title: "Before and After",
      intro:
        "These scenarios show what changes when Eduboxx replaces fragmented educational delivery with an integrated deployment model.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Before: Institutions needed reliable large-scale educational video delivery across multiple environments, but infrastructure limitations made consistent performance difficult.",
          after:
            "After: Eduboxx supported large-scale streaming delivery, helping serve thousands of students and enabling millions of educational streams in real learning environments.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Before: Large educational networks needed a scalable way to deliver content across multiple sites with varying operational realities.",
          after:
            "After: Eduboxx created a stable and scalable infrastructure model capable of supporting simultaneous access across distributed educational environments.",
        },
        {
          title: "From Fragmentation to System",
          before:
            "Before: Delivery, operations, and visibility were disconnected, making scale difficult and procurement harder to justify.",
          after:
            "After: Eduboxx created one understandable deployment story — visible infrastructure outside, integrated intelligence and learning power inside.",
        },
      ],
    },
    licensing: {
      title: "Brazil Licensing Structure",
      body:
        "DatanexAI acts as the master licensing agent for Brazil, leading with Eduboxx as the visible and physically demonstrable education system while delivering the integrated value of EduInsights, EduFlux, and EduAIKit as part of the same platform.",
      bullets: [
        "One system, one procurement story",
        "Physical deployment with integrated digital intelligence",
        "Proven foundation for Brazil market entry",
        "Scalable from pilot to large public-sector rollout",
      ],
    },
    contact: {
      title: "Let’s Build the Right Brazil Deployment",
      intro:
        "Start a conversation around pilots, regional implementations, licensing, or large-scale public-sector education deployment in Brazil.",
      form: {
        name: "Name",
        org: "Organization",
        email: "Email",
        region: "Country / Region",
        interest: "Primary Interest",
        message: "Message",
        options: [
          "Eduboxx Pilot",
          "Government Deployment",
          "Regional Rollout",
          "Licensing Partnership",
        ],
        submit: "Submit Inquiry",
      },
      note:
        "This front-end form is ready to connect to your CRM, backend endpoint, or WordPress handler.",
    },
    footer: {
      left: "DatanexAI",
      right: "Brazil master licensing agent for the Eduboxx integrated education system.",
    },
  },
  pt: {
    nav: {
      speed: "Velocidade Total",
      proof: "Provas",
      problem: "Problema",
      solution: "Solução",
      products: "Plataforma",
      flow: "Fluxo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamento Brasil",
      contact: "Contato",
    },
    tagline: "Inteligência de Aprendizagem. Construída sobre pedagogia. Movida por IA.",
    hero: {
      eyebrow: "DatanexAI • Agente Master de Licenciamento no Brasil",
      title:
        "Eduboxx Transforma Tecnologia Educacional em Algo que Governos Podem Ver, Confiar e Implantar",
      subtitle:
        "Eduboxx é a implantação física de um sistema completo de tecnologia educacional para o Brasil — combinando infraestrutura visível com aprendizagem personalizada gerada a partir dos planos de aula dos seus professores, avaliação e classificação de admissões e desafios baseados em simulação, todos alinhados à BNCC e construídos sobre pedagogia real.",
      primaryCta: "Solicitar um Piloto",
      secondaryCta: "Explorar Expansão no Brasil",
      stats: [
        "Comprovado no Brasil",
        "Milhões de streams entregues",
        "Milhares de alunos atendidos",
        "Construído para escala pública",
      ],
      sideTitle: "Por que esta mensagem funciona",
      sideBullets: [
        "Começa com algo tangível e fisicamente compreensível.",
        "Transforma uma plataforma complexa em uma história clara de implantação.",
        "Prova que o sistema já funcionou em escala no Brasil.",
        "Cria urgência sem parecer abstrato ou experimental.",
      ],
    },
    speed: {
      title: "Cada aluno em velocidade máxima — sem sobrecarregar sua internet.",
      subtitle:
        "O Eduboxx entrega a aprendizagem rica e personalizada do EduFlux para uma sala de aula inteira ao mesmo tempo — a partir de um nó local dentro da escola, para que 30 alunos transmitindo aulas e simulações não levem a conexão ao limite.",
      problem:
        "Uma escola pode ter internet e ainda assim travar. Uma conexão compartilhada não foi feita para uma turma inteira puxando vídeo, simulações e conteúdo personalizado no mesmo instante. Quanto mais envolvente o conteúdo, pior o gargalo. 'Conectado' não significa 'pronto para 30 alunos ao mesmo tempo.'",
      features: [
        "Entrega simultânea em escala de sala de aula: o Eduboxx serve o conteúdo a partir de um nó local pela rede da própria escola, para que cada aluno receba aprendizagem em velocidade e qualidade máximas ao mesmo tempo — sem buffering, sem fila.",
        "Sua conexão fica livre: o conteúdo pesado é entregue localmente; o link da internet só carrega sincronização leve. Outras turmas, sistemas administrativos e a banda da escola não ficam sufocados.",
        "Feito para conteúdo rico: aulas em vídeo, simulações do EduAIKit, a voz do Teli — a mídia pesada e envolvente que a entrega só pela nuvem tem dificuldade em servir para muitos alunos ao mesmo tempo.",
        "Funciona também onde a conectividade é limitada: em escolas com internet lenta, compartilhada ou instável, o Eduboxx mantém a aprendizagem rodando em qualidade máxima.",
        "Sincroniza quando dá: progresso e sinais voltam ao registro de aprendizagem quando a conexão permite, mantendo o registro completo.",
      ],
      differentiation:
        "Plataformas que dependem só da nuvem ficam lentas exatamente quando a sala de aula mais precisa — quando todo mundo está aprendendo ao mesmo tempo. O Eduboxx entrega para cada aluno em velocidade máxima e deixa sua conexão com a internet livre para tudo o mais.",
      cta: "Leve aprendizagem em velocidade máxima para cada sala de aula →",
    },
    proof: {
      title: "Comprovado em Escala no Brasil",
      intro:
        "Eduboxx não é um conceito. Já foi implementado com sucesso em grandes ambientes educacionais brasileiros, entregando streaming educacional em larga escala e acesso em sala de aula em configurações operacionais reais.",
      stats: [
        "Milhões de streams entregues",
        "Milhares de alunos alcançados",
        "Experiência de implantação multi-site",
        "Validado em ambientes educacionais reais",
      ],
      institutionsTitle: "Implementado em",
      description:
        "Essas implantações provaram que o Eduboxx pode entregar conteúdo educacional confiável em escala em ambientes de aprendizagem complexos — criando a base para o modelo integrado DatanexAI, agora sendo posicionado para o Brasil.",
    },
    problem: {
      title: "O Problema",
      intro:
        "Sistemas educacionais querem modernização, visibilidade e melhores resultados de aprendizagem — mas a maioria das iniciativas falha muito antes de criar uma transformação real.",
      cards: [
        {
          title: "Realidade da Infraestrutura",
          text: "Muitas escolas e redes ainda operam em ambientes onde largura de banda, confiabilidade e acesso simultâneo permanecem como grandes limitações.",
        },
        {
          title: "Sistemas Fragmentados",
          text: "Entrega de conteúdo, analytics, operações e suporte ao aprendizado frequentemente estão espalhados em ferramentas desconectadas que criam atrito e confusão.",
        },
        {
          title: "Narrativas Fracas de Aquisição",
          text: "Compradores do setor público frequentemente têm dificuldade em confiar ou avaliar soluções apresentadas apenas como software, dashboards ou abstrações na nuvem.",
        },
        {
          title: "Falha do Piloto à Escala",
          text: "Mesmo pilotos promissores frequentemente colapsam porque o modelo nunca foi projetado para se tornar um sistema operacional escalável.",
        },
      ],
    },
    solution: {
      title: "Por Que o Eduboxx Vence",
      intro:
        "Eduboxx vence porque começa onde a confiança começa: com algo visível, físico e implantável — e então entrega a plataforma completa dentro dessa implantação.",
      cards: [
        {
          title: "Infraestrutura Visível",
          text: "Eduboxx dá a governos e instituições algo tangível para avaliar, implantar e expandir.",
        },
        {
          title: "Integrado por Design",
          text: "EduInsights para avaliação e classificação de admissões, EduFlux para aprendizagem personalizada e EduAIKit para desafios baseados em simulação — capacidades integradas dentro do sistema Eduboxx, compartilhando um único registro de aprendizagem.",
        },
        {
          title: "Operacionalmente Claro",
          text: "Uma plataforma. Um caminho de implantação. Uma história do piloto à escala.",
        },
        {
          title: "Construído para Expansão",
          text: "O modelo é projetado para ir de implementações reais a oportunidades de expansão regional, estadual e nacional.",
        },
      ],
    },
    products: {
      title: "Dentro da Plataforma Eduboxx",
      intro:
        "Eduboxx é a camada de implantação visível. Dentro dele está um ecossistema completo de tecnologia educacional — construído sobre pedagogia, movido por IA e alinhado à BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestrutura Física",
          logoKey: "eduboxx",
          features: [
            "Camada de implantação local para escolas e redes",
            "Infraestrutura visível e tangível para confiança do setor público",
            "Suporta entrega de conteúdo em larga escala em ambientes com restrições",
            "Cria o gateway físico para adoção completa da plataforma",
          ],
        },
        {
          name: "EduFlux",
          tag: "Inteligência de Aprendizagem Personalizada",
          logoKey: "eduflux",
          features: [
            "Personalização generativa: lê o plano de aula de cada professor e cria a lição, o dever de casa e a prática para cada aluno — não retirados de um banco fixo",
            "Alinhado à BNCC: cada lição, tarefa e avaliação gerada mapeia para habilidades da BNCC — com relatórios por habilidade, para que professores e coordenadores vejam cobertura e domínio, não apenas atividade",
            "O tutor Teli dentro do trabalho: um tutor de IA que orienta os alunos em tempo real, com voz e texto",
            "Desenvolve Power Skills e Core Attributes — as competências transferíveis por trás de estruturas como as Approaches to Learning do IB",
          ],
        },
        {
          name: "EduInsights",
          tag: "Avaliação e Classificação de Admissões",
          logoKey: "eduinsights",
          features: [
            "Detecção de sinais além de uma única nota — revela como o candidato pensa, aprende e cresce",
            "Avaliação mais justa e completa, que compara candidatos por mais do que conteúdo memorizado",
            "Informa classificação e suporte desde o primeiro dia, não apenas aprovação ou rejeição",
            "Alimenta o mesmo registro de aprendizagem que acompanha o aluno até a sala de aula",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizado Baseado em Simulação · Parte do EduFlux",
          logoKey: "eduaikit",
          features: [
            "Desafios baseados em simulação: os alunos raciocinam em cenários e tomam decisões, em vez de apenas memorizar fatos",
            "Forte também fora das STEM — história, ciências sociais, ética, artes — onde bancos de questões fixos não bastam",
            "Gerados a partir da própria aula, personalizados para cada aluno",
            "Desenvolve raciocínio, perspectiva, uso de evidências e comunicação",
          ],
        },
      ],
      closing:
        "Juntos, esses componentes tornam o Eduboxx muito mais que uma caixa de entrega. Eles o tornam a expressão física de um sistema operacional educacional completo — generativo, liderado pela pedagogia, alinhado à BNCC e construído sobre um único registro de aprendizagem que acompanha cada aluno em todas as etapas.",
    },
    flow: {
      title: "Como o Sistema Funciona",
      intro:
        "O modelo Eduboxx é projetado para ir da implantação visível ao impacto educacional e operacional mensurável.",
      steps: [
        {
          title: "1. Implantar",
          text: "Instalar o Eduboxx em escolas piloto, campi, salas de aula ou regiões-alvo.",
        },
        {
          title: "2. Entregar",
          text: "Fornecer acesso confiável a conteúdo educacional e serviços em ambientes escolares reais.",
        },
        {
          title: "3. Avaliar e Classificar",
          text: "Usar o EduInsights para avaliar candidatos além de uma única nota e informar classificação e suporte desde o primeiro dia.",
        },
        {
          title: "4. Personalizar a Aprendizagem",
          text: "Usar o EduFlux para gerar aulas personalizadas a partir do plano de cada professor — alinhadas à BNCC, com o tutor Teli orientando os alunos no trabalho.",
        },
        {
          title: "5. Construir Raciocínio",
          text: "Usar o EduAIKit para transformar aulas em desafios baseados em simulação em todas as disciplinas, incluindo as não-STEM.",
        },
        {
          title: "6. Escalar",
          text: "Expandir da implementação piloto para expansões municipais, estaduais ou nacionais.",
        },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx cria valor em múltiplos níveis: operacionalmente, administrativamente, estrategicamente e educacionalmente.",
      cards: [
        {
          title: "Entrega Mais Confiável",
          text: "Estabilizar o acesso a conteúdo educacional em ambientes escolares do mundo real.",
        },
        {
          title: "Aprendizagem Personalizada em Escala",
          text: "Cada aluno recebe instrução gerada a partir dos planos de aula dos seus professores, alinhada à BNCC, com o tutor Teli orientando o trabalho.",
        },
        {
          title: "Sinal Mais Rico em Admissões",
          text: "Equipes de admissão recebem sinais mais completos do que uma única nota, e o que é aprendido sobre cada candidato informa a classificação e o suporte desde o primeiro dia.",
        },
        {
          title: "Raciocínio Mais Profundo, em Todas as Disciplinas",
          text: "Desafios baseados em simulação desenvolvem competências transferíveis — inclusive em disciplinas não-STEM onde bancos de questões fixos não bastam.",
        },
      ],
    },
    cases: {
      title: "Antes e Depois",
      intro:
        "Esses cenários mostram o que muda quando o Eduboxx substitui a entrega educacional fragmentada por um modelo de implantação integrado.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Antes: Instituições precisavam de entrega confiável de vídeo educacional em larga escala em múltiplos ambientes, mas limitações de infraestrutura dificultavam o desempenho consistente.",
          after:
            "Depois: Eduboxx suportou entrega de streaming em larga escala, ajudando a atender milhares de alunos e possibilitando milhões de streams educacionais em ambientes reais de aprendizagem.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Antes: Grandes redes educacionais precisavam de uma forma escalável de entregar conteúdo em múltiplos sites com diferentes realidades operacionais.",
          after:
            "Depois: Eduboxx criou um modelo de infraestrutura estável e escalável capaz de suportar acesso simultâneo em ambientes educacionais distribuídos.",
        },
        {
          title: "Da Fragmentação ao Sistema",
          before:
            "Antes: Entrega, operações e visibilidade eram desconectadas, tornando a escala difícil e a aquisição mais difícil de justificar.",
          after:
            "Depois: Eduboxx criou uma história de implantação compreensível — infraestrutura visível por fora, inteligência integrada e poder de aprendizado por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estrutura de Licenciamento Brasil",
      body:
        "DatanexAI atua como agente master de licenciamento para o Brasil, liderando com o Eduboxx como o sistema educacional visível e fisicamente demonstrável, enquanto entrega o valor integrado de EduInsights, EduFlux e EduAIKit como parte da mesma plataforma.",
      bullets: [
        "Um sistema, uma história de aquisição",
        "Implantação física com inteligência digital integrada",
        "Base comprovada para entrada no mercado brasileiro",
        "Escalável do piloto à grande expansão do setor público",
      ],
    },
    contact: {
      title: "Vamos Construir a Implantação Certa no Brasil",
      intro:
        "Inicie uma conversa sobre pilotos, implementações regionais, licenciamento ou implantação educacional de grande escala no setor público no Brasil.",
      form: {
        name: "Nome",
        org: "Organização",
        email: "E-mail",
        region: "País / Região",
        interest: "Interesse Principal",
        message: "Mensagem",
        options: [
          "Piloto Eduboxx",
          "Implantação Governamental",
          "Expansão Regional",
          "Parceria de Licenciamento",
        ],
        submit: "Enviar Consulta",
      },
      note:
        "Este formulário está pronto para conectar ao seu CRM, endpoint de backend ou handler do WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente master de licenciamento no Brasil para o sistema educacional integrado Eduboxx.",
    },
  },
  es: {
    nav: {
      speed: "Velocidad Total",
      proof: "Pruebas",
      problem: "Problema",
      solution: "Solución",
      products: "Plataforma",
      flow: "Flujo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamiento Brasil",
      contact: "Contacto",
    },
    tagline: "Inteligencia de Aprendizaje. Construida sobre pedagogía. Impulsada por IA.",
    hero: {
      eyebrow: "DatanexAI • Agente Master de Licenciamiento en Brasil",
      title:
        "Eduboxx Convierte la Tecnología Educativa en Algo que los Gobiernos Pueden Ver, Confiar e Implementar",
      subtitle:
        "Eduboxx es la implementación física de un sistema completo de tecnología educativa para Brasil — combinando infraestructura visible con aprendizaje personalizado generado a partir de los planes de clase de sus profesores, evaluación y clasificación de admisiones, y desafíos basados en simulación, todos alineados al currículo de la BNCC en Brasil y construidos sobre pedagogía real.",
      primaryCta: "Solicitar un Piloto",
      secondaryCta: "Explorar Expansión en Brasil",
      stats: [
        "Comprobado en Brasil",
        "Millones de streams entregados",
        "Miles de estudiantes atendidos",
        "Construido para escala pública",
      ],
      sideTitle: "Por qué este mensaje funciona",
      sideBullets: [
        "Comienza con algo tangible y físicamente comprensible.",
        "Transforma una plataforma compleja en una historia clara de implementación.",
        "Demuestra que el sistema ya funcionó a escala en Brasil.",
        "Crea urgencia sin sonar abstracto o experimental.",
      ],
    },
    speed: {
      title: "Cada estudiante a máxima velocidad — sin sobrecargar su internet.",
      subtitle:
        "Eduboxx entrega el aprendizaje rico y personalizado de EduFlux a un aula entera al mismo tiempo — desde un nodo local dentro de la escuela, para que 30 estudiantes transmitiendo lecciones y simulaciones no lleven la conexión al límite.",
      problem:
        "Una escuela puede tener internet y aun así trabarse. Una conexión compartida no fue hecha para un aula entera tirando video, simulaciones y contenido personalizado en el mismo momento. Cuanto más atractivo el contenido, peor el cuello de botella. 'Conectado' no significa 'listo para 30 estudiantes a la vez.'",
      features: [
        "Entrega simultánea a escala de aula: Eduboxx sirve el contenido desde un nodo local por la red de la propia escuela, para que cada estudiante reciba aprendizaje a velocidad y calidad máximas al mismo tiempo — sin buffering, sin fila.",
        "Su conexión queda libre: el contenido pesado se entrega localmente; el enlace a internet solo lleva sincronización liviana. Otras clases, sistemas administrativos y el ancho de banda de la escuela no quedan ahogados.",
        "Hecho para contenido rico: lecciones en video, simulaciones de EduAIKit, la voz de Teli — los medios pesados y atractivos que la entrega solo por la nube tiene dificultad para servir a muchos estudiantes a la vez.",
        "Funciona también donde la conectividad es limitada: en escuelas con internet lenta, compartida o inestable, Eduboxx mantiene el aprendizaje funcionando a calidad máxima.",
        "Sincroniza cuando puede: el progreso y las señales vuelven al registro de aprendizaje cuando la conexión lo permite, manteniendo el registro completo.",
      ],
      differentiation:
        "Las plataformas que dependen solo de la nube se vuelven lentas justo cuando el aula más las necesita — cuando todos están aprendiendo al mismo tiempo. Eduboxx entrega a cada estudiante a máxima velocidad y deja su conexión a internet libre para todo lo demás.",
      cta: "Lleve aprendizaje a máxima velocidad a cada aula →",
    },
    proof: {
      title: "Comprobado a Escala en Brasil",
      intro:
        "Eduboxx no es un concepto. Ya ha sido implementado exitosamente en grandes entornos educativos brasileños, entregando streaming educativo a gran escala y acceso en aulas en configuraciones operativas reales.",
      stats: [
        "Millones de streams entregados",
        "Miles de estudiantes alcanzados",
        "Experiencia de implementación multi-sitio",
        "Validado en entornos educativos reales",
      ],
      institutionsTitle: "Implementado en",
      description:
        "Estas implementaciones demostraron que Eduboxx puede entregar contenido educativo confiable a escala en entornos de aprendizaje complejos — creando la base para el modelo integrado DatanexAI ahora posicionándose para Brasil.",
    },
    problem: {
      title: "El Problema",
      intro:
        "Los sistemas educativos quieren modernización, visibilidad y mejores resultados de aprendizaje — pero la mayoría de las iniciativas fracasan mucho antes de crear una transformación real.",
      cards: [
        {
          title: "Realidad de Infraestructura",
          text: "Muchas escuelas y redes aún operan en entornos donde el ancho de banda, la confiabilidad y el acceso simultáneo siguen siendo grandes limitaciones.",
        },
        {
          title: "Sistemas Fragmentados",
          text: "Entrega de contenido, analítica, operaciones y soporte al aprendizaje frecuentemente están dispersos en herramientas desconectadas que crean fricción y confusión.",
        },
        {
          title: "Narrativas Débiles de Adquisición",
          text: "Los compradores del sector público frecuentemente tienen dificultad para confiar o evaluar soluciones presentadas solo como software, dashboards o abstracciones en la nube.",
        },
        {
          title: "Fallo del Piloto a la Escala",
          text: "Incluso pilotos prometedores frecuentemente colapsan porque el modelo nunca fue diseñado para convertirse en un sistema operativo escalable.",
        },
      ],
    },
    solution: {
      title: "Por Qué Eduboxx Gana",
      intro:
        "Eduboxx gana porque comienza donde comienza la confianza: con algo visible, físico e implementable — y luego entrega la plataforma completa dentro de esa implementación.",
      cards: [
        {
          title: "Infraestructura Visible",
          text: "Eduboxx da a gobiernos e instituciones algo tangible para evaluar, implementar y expandir.",
        },
        {
          title: "Integrado por Diseño",
          text: "EduInsights para evaluación y clasificación de admisiones, EduFlux para aprendizaje personalizado y EduAIKit para desafíos basados en simulación — capacidades integradas dentro del sistema Eduboxx, compartiendo un único registro de aprendizaje.",
        },
        {
          title: "Operacionalmente Claro",
          text: "Una plataforma. Un camino de implementación. Una historia del piloto a la escala.",
        },
        {
          title: "Construido para Expansión",
          text: "El modelo está diseñado para ir de implementaciones reales a oportunidades de expansión regional, estatal y nacional.",
        },
      ],
    },
    products: {
      title: "Dentro de la Plataforma Eduboxx",
      intro:
        "Eduboxx es la capa de implementación visible. Dentro de él se encuentra un ecosistema completo de tecnología educativa — construido sobre pedagogía, impulsado por IA y alineado al currículo de la BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestructura Física",
          logoKey: "eduboxx",
          features: [
            "Capa de implementación local para escuelas y redes",
            "Infraestructura visible y tangible para confianza del sector público",
            "Soporta entrega de contenido a gran escala en entornos con restricciones",
            "Crea la puerta de entrada física para adopción completa de la plataforma",
          ],
        },
        {
          name: "EduFlux",
          tag: "Inteligencia de Aprendizaje Personalizado",
          logoKey: "eduflux",
          features: [
            "Personalización generativa: lee el plan de cada profesor y crea la lección, la tarea y la práctica para cada estudiante — no extraídos de un banco fijo",
            "Alineado a la BNCC: cada lección, tarea y evaluación generada se mapea a habilidades de la BNCC — con reportes por habilidad, para que profesores y coordinadores vean cobertura y dominio, no solo actividad",
            "El tutor Teli dentro del trabajo: un tutor de IA que guía a los estudiantes en tiempo real, con voz y texto",
            "Desarrolla Power Skills y Core Attributes — las competencias transferibles detrás de marcos como las Approaches to Learning del IB",
          ],
        },
        {
          name: "EduInsights",
          tag: "Evaluación y Clasificación de Admisiones",
          logoKey: "eduinsights",
          features: [
            "Detección de señales más allá de una sola calificación — revela cómo el postulante piensa, aprende y crece",
            "Evaluación más justa y completa, que compara postulantes por más que contenido memorizado",
            "Informa la clasificación y el apoyo desde el primer día, no solo admisión o rechazo",
            "Alimenta el mismo registro de aprendizaje que acompaña al estudiante hasta el aula",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizaje Basado en Simulación · Parte de EduFlux",
          logoKey: "eduaikit",
          features: [
            "Desafíos basados en simulación: los estudiantes razonan en escenarios y toman decisiones, en lugar de solo recordar hechos",
            "Fuerte también fuera de STEM — historia, ciencias sociales, ética, artes — donde los bancos de preguntas fijos se quedan cortos",
            "Generados a partir de la propia lección, personalizados para cada estudiante",
            "Desarrolla razonamiento, perspectiva, uso de evidencia y comunicación",
          ],
        },
      ],
      closing:
        "Juntos, estos componentes hacen de Eduboxx mucho más que una caja de entrega. Lo convierten en la expresión física de un sistema operativo educativo completo — generativo, liderado por la pedagogía, alineado a la BNCC y construido sobre un único registro de aprendizaje que acompaña a cada estudiante en cada etapa.",
    },
    flow: {
      title: "Cómo Funciona el Sistema",
      intro:
        "El modelo Eduboxx está diseñado para ir de la implementación visible al impacto educativo y operativo medible.",
      steps: [
        {
          title: "1. Implementar",
          text: "Instalar Eduboxx en escuelas piloto, campus, aulas o regiones objetivo.",
        },
        {
          title: "2. Entregar",
          text: "Proporcionar acceso confiable a contenido educativo y servicios en entornos escolares reales.",
        },
        {
          title: "3. Evaluar y Clasificar",
          text: "Usar EduInsights para evaluar postulantes más allá de una sola calificación e informar la clasificación y el apoyo desde el primer día.",
        },
        {
          title: "4. Personalizar el Aprendizaje",
          text: "Usar EduFlux para generar lecciones personalizadas a partir del plan de cada profesor — alineadas a la BNCC, con el tutor Teli guiando a los estudiantes en el trabajo.",
        },
        {
          title: "5. Construir Razonamiento",
          text: "Usar EduAIKit para convertir lecciones en desafíos basados en simulación en todas las materias, incluyendo no-STEM.",
        },
        {
          title: "6. Escalar",
          text: "Expandir de la implementación piloto a expansiones municipales, estatales o nacionales.",
        },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx crea valor en múltiples niveles: operacionalmente, administrativamente, estratégicamente y educacionalmente.",
      cards: [
        {
          title: "Entrega Más Confiable",
          text: "Estabilizar el acceso a contenido educativo en entornos escolares del mundo real.",
        },
        {
          title: "Aprendizaje Personalizado a Escala",
          text: "Cada estudiante recibe instrucción generada a partir de los planes de clase de sus profesores, alineada a la BNCC, con el tutor Teli guiando el trabajo.",
        },
        {
          title: "Señal Más Rica en Admisiones",
          text: "Los equipos de admisiones reciben señales más completas que una sola calificación, y lo aprendido sobre cada postulante informa la clasificación y el apoyo desde el primer día.",
        },
        {
          title: "Razonamiento Más Profundo, en Cada Materia",
          text: "Desafíos basados en simulación desarrollan competencias transferibles — incluso en materias no-STEM donde los bancos de preguntas fijos se quedan cortos.",
        },
      ],
    },
    cases: {
      title: "Antes y Después",
      intro:
        "Estos escenarios muestran qué cambia cuando Eduboxx reemplaza la entrega educativa fragmentada con un modelo de implementación integrado.",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before:
            "Antes: Las instituciones necesitaban entrega confiable de video educativo a gran escala en múltiples entornos, pero las limitaciones de infraestructura dificultaban el rendimiento consistente.",
          after:
            "Después: Eduboxx soportó entrega de streaming a gran escala, ayudando a atender miles de estudiantes y posibilitando millones de streams educativos en entornos reales de aprendizaje.",
        },
        {
          title: "Anhanguera / Kroton",
          before:
            "Antes: Grandes redes educativas necesitaban una forma escalable de entregar contenido en múltiples sitios con diferentes realidades operativas.",
          after:
            "Después: Eduboxx creó un modelo de infraestructura estable y escalable capaz de soportar acceso simultáneo en entornos educativos distribuidos.",
        },
        {
          title: "De la Fragmentación al Sistema",
          before:
            "Antes: Entrega, operaciones y visibilidad estaban desconectadas, haciendo la escala difícil y la adquisición más difícil de justificar.",
          after:
            "Después: Eduboxx creó una historia de implementación comprensible — infraestructura visible por fuera, inteligencia integrada y poder de aprendizaje por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estructura de Licenciamiento Brasil",
      body:
        "DatanexAI actúa como agente master de licenciamiento para Brasil, liderando con Eduboxx como el sistema educativo visible y físicamente demostrable, mientras entrega el valor integrado de EduInsights, EduFlux y EduAIKit como parte de la misma plataforma.",
      bullets: [
        "Un sistema, una historia de adquisición",
        "Implementación física con inteligencia digital integrada",
        "Base comprobada para entrada al mercado brasileño",
        "Escalable del piloto a gran expansión del sector público",
      ],
    },
    contact: {
      title: "Construyamos la Implementación Correcta en Brasil",
      intro:
        "Inicie una conversación sobre pilotos, implementaciones regionales, licenciamiento o implementación educativa de gran escala en el sector público en Brasil.",
      form: {
        name: "Nombre",
        org: "Organización",
        email: "Correo Electrónico",
        region: "País / Región",
        interest: "Interés Principal",
        message: "Mensaje",
        options: [
          "Piloto Eduboxx",
          "Implementación Gubernamental",
          "Expansión Regional",
          "Asociación de Licenciamiento",
        ],
        submit: "Enviar Consulta",
      },
      note:
        "Este formulario está listo para conectar a su CRM, endpoint de backend o handler de WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente master de licenciamiento en Brasil para el sistema educativo integrado Eduboxx.",
    },
  },
};

function usePageStyles() {
  useEffect(() => {
    const styleId = "datanexai-full-public-images-v3";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      * { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body {
        margin: 0;
        font-family: Inter, Arial, Helvetica, sans-serif;
        background:
          radial-gradient(circle at top left, rgba(59, 130, 246, 0.10), transparent 30%),
          radial-gradient(circle at top right, rgba(6, 182, 212, 0.08), transparent 28%),
          linear-gradient(180deg, #f8fbff 0%, #eef6ff 42%, #e8f2fb 100%);
        color: #0f172a;
      }
      a { color: inherit; text-decoration: none; }
      .page-shell { min-height: 100vh; }
      .container {
        width: min(1180px, calc(100% - 40px));
        margin: 0 auto;
      }
      .nav {
        position: sticky;
        top: 0;
        z-index: 100;
        backdrop-filter: blur(14px);
        background: rgba(255, 255, 255, 0.88);
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
      }
      .nav-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 18px;
        padding: 16px 0;
      }
      .brand-wrap {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .logo-main {
        height: 44px;
        object-fit: contain;
      }
      .brand-sub {
        font-size: 0.76rem;
        color: rgba(15, 23, 42, 0.62);
      }
      .nav-links {
        display: flex;
        align-items: center;
        gap: 14px;
        flex-wrap: wrap;
        color: rgba(15, 23, 42, 0.82);
        font-size: 0.92rem;
      }
      .lang-switcher {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px;
        border-radius: 999px;
        background: rgba(255,255,255,0.9);
        border: 1px solid rgba(15, 23, 42, 0.08);
      }
      .lang-btn {
        border: 0;
        background: transparent;
        color: rgba(15, 23, 42, 0.72);
        padding: 8px 12px;
        border-radius: 999px;
        cursor: pointer;
        font-weight: 800;
      }
      .lang-btn.active {
        background: linear-gradient(135deg, #2563eb, #0891b2);
        color: #fff;
      }
      .hero {
        padding: 96px 0 72px;
      }
      .hero-grid {
        display: grid;
        grid-template-columns: 1.2fr 0.85fr;
        gap: 28px;
        align-items: stretch;
      }
      .eyebrow {
        display: inline-flex;
        align-items: center;
        padding: 10px 14px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.10);
        border: 1px solid rgba(37, 99, 235, 0.16);
        color: #1d4ed8;
        font-size: 0.82rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        margin-bottom: 18px;
      }
      .hero-product-logo {
        height: 64px;
        object-fit: contain;
        margin-bottom: 18px;
      }
      h1 {
        margin: 0 0 18px;
        font-size: clamp(2.45rem, 4vw, 4.5rem);
        line-height: 1.02;
        letter-spacing: -0.035em;
        color: #0f172a;
      }
      .lead {
        margin: 0 0 32px;
        font-size: 1.09rem;
        line-height: 1.8;
        color: rgba(15, 23, 42, 0.78);
        max-width: 760px;
      }
      .cta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 14px;
        margin-bottom: 30px;
      }
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 16px;
        padding: 15px 20px;
        font-size: 0.99rem;
        font-weight: 800;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      }
      .btn:hover {
        transform: translateY(-1px);
      }
      .btn-primary {
        background: linear-gradient(135deg, #1d4ed8 0%, #0891b2 100%);
        color: #fff;
        box-shadow: 0 16px 32px rgba(29, 78, 216, 0.22);
      }
      .btn-secondary {
        background: #ffffff;
        color: #0f172a;
        border: 1px solid rgba(15, 23, 42, 0.12);
        box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
      }
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 12px;
      }
      .glass-card,
      .card,
      .feature-card,
      .step-card,
      .case-card,
      .contact-card,
      .licensing-box,
      .proof-band {
        background: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 24px;
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.07);
      }
      .glass-card,
      .proof-band,
      .licensing-box {
        padding: 26px;
      }
      .mini-kicker {
        color: #0369a1;
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 800;
        margin-bottom: 10px;
      }
      .card-title {
        margin: 0 0 14px;
        font-size: 1.32rem;
        font-weight: 800;
        color: #0f172a;
      }
      .bullet-list,
      .feature-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 12px;
      }
      .bullet-list li,
      .stat-item,
      .feature-list li,
      .proof-stat {
        padding: 14px 16px;
        border-radius: 16px;
        background: #f3f8ff;
        color: #0f172a;
        line-height: 1.55;
      }
      section {
        padding: 42px 0 64px;
      }
      .section-header {
        max-width: 930px;
        margin-bottom: 26px;
      }
      .section-header h2 {
        margin: 0 0 14px;
        font-size: clamp(1.85rem, 3vw, 3rem);
        letter-spacing: -0.03em;
        color: #0f172a;
      }
      .section-header p {
        margin: 0;
        color: rgba(15, 23, 42, 0.72);
        line-height: 1.8;
        font-size: 1.03rem;
      }
      .cards-3,
      .cards-4,
      .steps-grid,
      .contact-grid,
      .cases-grid,
      .proof-grid {
        display: grid;
        gap: 20px;
      }
      .cards-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .cards-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .steps-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .cases-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .proof-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .contact-grid {
        grid-template-columns: 0.92fr 1.08fr;
        align-items: start;
      }
      .card,
      .feature-card,
      .step-card,
      .case-card,
      .contact-card {
        padding: 24px;
      }
      .card h3,
      .feature-card h3,
      .step-card h3,
      .case-card h3,
      .contact-card h3 {
        margin: 0 0 12px;
        font-size: 1.18rem;
        color: #0f172a;
      }
      .card p,
      .feature-card p,
      .step-card p,
      .case-card p,
      .contact-card p {
        margin: 0;
        color: rgba(15, 23, 42, 0.78);
        line-height: 1.7;
      }
      .proof-client-logos {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 26px;
        margin-top: 20px;
      }
      .proof-client-logos img {
        height: 52px;
        max-width: 180px;
        object-fit: contain;
        filter: grayscale(100%);
        opacity: 0.78;
        transition: all 0.2s ease;
      }
      .proof-client-logos img:hover {
        filter: grayscale(0%);
        opacity: 1;
        transform: translateY(-2px);
      }
      .feature-tag {
        display: inline-block;
        margin-bottom: 10px;
        padding: 7px 10px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.10);
        color: #1d4ed8;
        font-size: 0.75rem;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .product-logo {
        height: 48px;
        object-fit: contain;
        margin-bottom: 14px;
      }
      .section-closing,
      .proof-description {
        margin-top: 22px;
        color: rgba(15, 23, 42, 0.74);
        line-height: 1.75;
      }
      .proof-band {
        margin-top: 22px;
      }
      .before-after {
        display: grid;
        gap: 12px;
        margin-top: 14px;
      }
      .before-box,
      .after-box {
        padding: 14px 16px;
        border-radius: 16px;
        background: #f3f8ff;
        color: #0f172a;
      }
      .before-label,
      .after-label {
        display: block;
        font-weight: 800;
        margin-bottom: 6px;
        color: #0f172a;
      }
      .licensing-box {
        padding: 26px;
        background: linear-gradient(135deg, rgba(219, 234, 254, 0.95), rgba(236, 254, 255, 0.95));
      }
      .licensing-box p {
        margin: 0 0 18px;
        color: rgba(15, 23, 42, 0.82);
        line-height: 1.8;
      }
      .licensing-points {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }
      .licensing-point {
        padding: 14px 16px;
        border-radius: 16px;
        background: rgba(255,255,255,0.78);
        color: #0f172a;
      }
      form {
        display: grid;
        gap: 14px;
      }
      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      label {
        display: grid;
        gap: 8px;
        font-size: 0.92rem;
        color: #0f172a;
        font-weight: 650;
      }
      input, select, textarea {
        width: 100%;
        border: 1px solid rgba(15, 23, 42, 0.12);
        background: rgba(255, 255, 255, 0.98);
        color: #0f172a;
        border-radius: 16px;
        padding: 14px 15px;
        font: inherit;
        outline: none;
      }
      input:focus, select:focus, textarea:focus {
        border-color: rgba(29, 78, 216, 0.35);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10);
      }
      textarea {
        min-height: 150px;
        resize: vertical;
      }
      .form-note {
        margin-top: 16px;
        font-size: 0.9rem;
        color: rgba(15, 23, 42, 0.62);
        line-height: 1.65;
      }
      footer {
        padding: 28px 0 46px;
        color: rgba(15, 23, 42, 0.62);
      }
      .footer-box {
        padding-top: 20px;
        border-top: 1px solid rgba(15, 23, 42, 0.08);
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 12px 16px;
      }
      @media (max-width: 1040px) {
        .hero-grid,
        .cards-4,
        .cards-3,
        .steps-grid,
        .cases-grid,
        .contact-grid,
        .proof-grid,
        .stats-grid,
        .licensing-points {
          grid-template-columns: 1fr;
        }
      }
      @media (max-width: 820px) {
        .nav-inner {
          flex-direction: column;
          align-items: flex-start;
        }
        .field-grid {
          grid-template-columns: 1fr;
        }
        .hero {
          padding-top: 52px;
          padding-bottom: 52px;
        }
        section {
          padding: 34px 0 52px;
        }
        .proof-client-logos img {
          height: 44px;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

export default function App() {
  usePageStyles();

  const [lang, setLang] = useState("en");
  const [formData, setFormData] = useState({
    name: "",
    org: "",
    email: "",
    region: "",
    interest: "",
    message: "",
  });

  const t = useMemo(() => CONTENT[lang], [lang]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert("Form ready to connect to your backend.");
  };

  const proofLogos = [
    { src: IMAGES.kroton, alt: "Kroton" },
    { src: IMAGES.anhanguera, alt: "Anhanguera" },
    { src: IMAGES.lfg, alt: "LFG" },
    { src: IMAGES.damasio, alt: "Damásio" },
  ];

  return (
    <div className="page-shell">
      <nav className="nav">
        <div className="container nav-inner">
          <div className="brand-wrap">
            <img src={IMAGES.datanex} alt="DatanexAI" className="logo-main" />
            <span className="brand-sub">{t.tagline}</span>
          </div>

          <div className="nav-links">
            <a href="#speed">{t.nav.speed}</a>
            <a href="#proof">{t.nav.proof}</a>
            <a href="#problem">{t.nav.problem}</a>
            <a href="#solution">{t.nav.solution}</a>
            <a href="#products">{t.nav.products}</a>
            <a href="#flow">{t.nav.flow}</a>
            <a href="#impact">{t.nav.impact}</a>
            <a href="#cases">{t.nav.cases}</a>
            <a href="#licensing">{t.nav.licensing}</a>
            <a href="#contact">{t.nav.contact}</a>

            <div className="lang-switcher">
              {["en", "pt", "es"].map((code) => (
                <button
                  key={code}
                  className={`lang-btn${lang === code ? " active" : ""}`}
                  onClick={() => setLang(code)}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <div className="eyebrow">{t.hero.eyebrow}</div>
              <img src={IMAGES.eduboxx} alt="Eduboxx" className="hero-product-logo" />
              <h1>{t.hero.title}</h1>
              <p className="lead">{t.hero.subtitle}</p>

              <div className="cta-row">
                <a className="btn btn-primary" href="#contact">{t.hero.primaryCta}</a>
                <a className="btn btn-secondary" href="#licensing">{t.hero.secondaryCta}</a>
              </div>

              <div className="stats-grid">
                {t.hero.stats.map((item) => (
                  <div className="stat-item" key={item}>{item}</div>
                ))}
              </div>
            </div>

            <aside className="glass-card">
              <div className="mini-kicker">Eduboxx First</div>
              <div className="card-title">{t.hero.sideTitle}</div>
              <ul className="bullet-list">
                {t.hero.sideBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        <section id="speed">
          <div className="container">
            <div className="section-header">
              <h2>{t.speed.title}</h2>
              <p>{t.speed.subtitle}</p>
            </div>

            <div className="glass-card">
              <p style={{ margin: 0, fontSize: "1.05rem", lineHeight: 1.8, color: "rgba(15, 23, 42, 0.85)" }}>
                {t.speed.problem}
              </p>
            </div>

            <ul className="feature-list" style={{ marginTop: 20 }}>
              {t.speed.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            <p className="section-closing">{t.speed.differentiation}</p>

            <div style={{ marginTop: 18 }}>
              <a className="btn btn-primary" href="#contact">{t.speed.cta}</a>
            </div>
          </div>
        </section>

        <section id="proof">
          <div className="container">
            <div className="section-header">
              <h2>{t.proof.title}</h2>
              <p>{t.proof.intro}</p>
            </div>

            <div className="proof-grid">
              {t.proof.stats.map((item) => (
                <div className="proof-stat" key={item}>{item}</div>
              ))}
            </div>

            <div className="proof-band">
              <div className="mini-kicker">{t.proof.institutionsTitle}</div>
              <div className="proof-client-logos">
                {proofLogos.map((logo) => (
                  <img key={logo.alt} src={logo.src} alt={logo.alt} />
                ))}
              </div>
              <p className="proof-description">{t.proof.description}</p>
            </div>
          </div>
        </section>

        <section id="problem">
          <div className="container">
            <div className="section-header">
              <h2>{t.problem.title}</h2>
              <p>{t.problem.intro}</p>
            </div>
            <div className="cards-4">
              {t.problem.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="solution">
          <div className="container">
            <div className="section-header">
              <h2>{t.solution.title}</h2>
              <p>{t.solution.intro}</p>
            </div>
            <div className="cards-4">
              {t.solution.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="products">
          <div className="container">
            <div className="section-header">
              <h2>{t.products.title}</h2>
              <p>{t.products.intro}</p>
            </div>

            <div className="cards-4">
              {t.products.cards.map((card) => (
                <article className="feature-card" key={card.name}>
                  <span className="feature-tag">{card.tag}</span>
                  <img src={IMAGES[card.logoKey]} alt={card.name} className="product-logo" />
                  <h3>{card.name}</h3>
                  <ul className="feature-list">
                    {card.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <p className="section-closing">{t.products.closing}</p>
          </div>
        </section>

        <section id="flow">
          <div className="container">
            <div className="section-header">
              <h2>{t.flow.title}</h2>
              <p>{t.flow.intro}</p>
            </div>
            <div className="steps-grid">
              {t.flow.steps.map((step) => (
                <article className="step-card" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="impact">
          <div className="container">
            <div className="section-header">
              <h2>{t.impact.title}</h2>
              <p>{t.impact.intro}</p>
            </div>
            <div className="cards-4">
              {t.impact.cards.map((card) => (
                <article className="card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="cases">
          <div className="container">
            <div className="section-header">
              <h2>{t.cases.title}</h2>
              <p>{t.cases.intro}</p>
            </div>

            <div className="cases-grid">
              {t.cases.cards.map((card) => (
                <article className="case-card" key={card.title}>
                  <h3>{card.title}</h3>
                  <div className="before-after">
                    <div className="before-box">
                      <span className="before-label">Before</span>
                      {card.before}
                    </div>
                    <div className="after-box">
                      <span className="after-label">After</span>
                      {card.after}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="licensing">
          <div className="container">
            <div className="section-header">
              <h2>{t.licensing.title}</h2>
            </div>
            <div className="licensing-box">
              <p>{t.licensing.body}</p>
              <div className="licensing-points">
                {t.licensing.bullets.map((bullet) => (
                  <div className="licensing-point" key={bullet}>{bullet}</div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="container contact-grid">
            <div className="contact-card">
              <h3>{t.contact.title}</h3>
              <p>{t.contact.intro}</p>
              <p className="form-note">{t.contact.note}</p>
            </div>

            <div className="contact-card">
              <form onSubmit={handleSubmit}>
                <div className="field-grid">
                  <label>
                    {t.contact.form.name}
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.contact.form.name}
                      required
                    />
                  </label>

                  <label>
                    {t.contact.form.org}
                    <input
                      name="org"
                      value={formData.org}
                      onChange={handleChange}
                      placeholder={t.contact.form.org}
                      required
                    />
                  </label>
                </div>

                <div className="field-grid">
                  <label>
                    {t.contact.form.email}
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.contact.form.email}
                      required
                    />
                  </label>

                  <label>
                    {t.contact.form.region}
                    <input
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                      placeholder={t.contact.form.region}
                    />
                  </label>
                </div>

                <label>
                  {t.contact.form.interest}
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select...</option>
                    {t.contact.form.options.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>

                <label>
                  {t.contact.form.message}
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.contact.form.message}
                  />
                </label>

                <button className="btn btn-primary" type="submit">
                  {t.contact.form.submit}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-box">
          <div>{t.footer.left}</div>
          <div>{t.footer.right}</div>
        </div>
      </footer>
    </div>
  );
}